import { useReactiveVar } from "@apollo/client";
import { ResizeObserver } from "@juggle/resize-observer";
import { useRef, useState, useMemo, useEffect } from "react";
import { Carousel } from "~/components/blocks/common/carousel";
import { isOpenProductModalVar, isOpenSampleViewModalVar } from "~/components/stores/detail-product/store";
import { runningInBrowser } from "~/const/browser";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import { useMatchMedia } from "~/hooks/use-match-media";
import type { G_VideoSamplePicture } from "~/lib/graphql";

const MODAL_MAX_WIDTH = 1024;
/** 横幅 : 230px + padding 8px * 2 */
const PC_MIN_WIDTH = 246;
/** 横幅 : 140px + padding 4px * 2 */
const SP_MIN_WIDTH = 148;

type VideoLegacyCarouselProps = {
  /** カルーセルを表示するために必要なデータ */
  gravureSeasonImageData: Pick<G_VideoSamplePicture, "image" | "imageLarge">[];
  /** 選択された画像の番号を親に渡すための関数 */
  onSelectedImageIndex: (idx: number) => void;
};

export const VideoLegacyCarousel = (props: VideoLegacyCarouselProps) => {
  const { gravureSeasonImageData, onSelectedImageIndex } = props;

  const isOpenProductModal = useReactiveVar(isOpenProductModalVar);
  const { isPcSize, isSpSize } = useMatchMedia();
  const { handleClickByDevice } = useHandleClickByDevice();

  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "LANDSCAPE" });

  const ref = useRef<HTMLDivElement>(null);
  const windowWidth = runningInBrowser ? window.innerWidth : 0;
  const [containerWidth, setContainerWidth] = useState(MODAL_MAX_WIDTH);

  const minWidth = isSpSize ? SP_MIN_WIDTH : PC_MIN_WIDTH;
  const displayItemCount = Math.floor(containerWidth / minWidth);

  const resizeObserver = useMemo(() => {
    return runningInBrowser
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            setContainerWidth(entry.contentRect.width);
          });
        })
      : undefined;
  }, []);

  const gravureCarouselItem = useMemo(() => {
    return (
      gravureSeasonImageData?.map((item, idx) => {
        return {
          // MEMO（rui-yanagida）: imageLargeがnullもしくは""の可能性あるので、その場合は`image`を表示するように制御
          ...imageSrcFormat({ imgSrc: item.imageLarge || item.image }),
          alt: `グラビア画像-${idx}`,
          borderRadius: "8px",
          aspectRatio: "9/16",
          ...handleClickByDevice(() => {
            onClickSampleImage(idx);
          }),
        };
      }) ?? []
    );
  }, [gravureSeasonImageData]);

  const effectContainerWidth = () => {
    if (!runningInBrowser) return;
    if (isOpenProductModal && isPcSize) return;

    setContainerWidth(window.innerWidth);
  };

  const carouselResizeHandler = () => {
    if (!ref.current) return;
    if (!resizeObserver) return;

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  };

  const onClickSampleImage = (idx: number) => {
    isOpenSampleViewModalVar(true);
    onSelectedImageIndex(idx);
  };

  useEffect(effectContainerWidth, []);
  useEffect(carouselResizeHandler, [windowWidth]);

  return (
    <Carousel
      displayItemCount={displayItemCount}
      ref={ref}
      items={gravureCarouselItem}
      skeletonSetting={{
        isShowSkeleton: !gravureSeasonImageData,
        aspectRatio: "9/16",
      }}
      isDisabledPreviewModal
    />
  );
};
